import * as React from 'react';

import classnames from 'classnames';

import styles from './textfield.module.css';

export type TextFieldProps = {
    // TODO: Figure out how to make this work.  Might need a newer flow than 0.53
    // ...HTMLInputElement,
    // There are rare case where you may want a textfield to be uncontrolled
    // (meaning you are not keeping its value in state anywhere). The primary
    // use case is for sensitive data (eg. a password) that you want to treat
    // as a black box.
    uncontrolled?: true;
    value: string;
    type?: HTMLInputElement['type'];
    onChange?: (arg1: string) => any;
    multiline?: boolean;
    rows?: number;
    min?: string;
    max?: string;
    textFieldRef?:
        | {
              current: HTMLInputElement | null | undefined;
          }
        | ((arg1?: HTMLInputElement | null | undefined) => void);
    autocomplete?: boolean;
    onEnter?: (arg1: React.KeyboardEvent<any>) => void;
    onEscape?: (arg1: React.KeyboardEvent<any>) => void;
    onKeyDown?: (arg1: React.KeyboardEvent<any>) => void;
    focusOnMount?: boolean;
    selectOnFocus?: boolean;
    blinkSuccess?: boolean;
    hasError?: boolean;
    noPadding?: boolean;
    noBorder?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    errorMessage?: string;
    placeholder?: string;
    fitContent?: boolean;
    size?: 'mini' | 'small' | 'normal' | 'medium' | 'big' | 'expand';
    inputUnit?: string;
    id?: string;
    ignorePasswordAutoFill?: boolean;
    customFooter?: React.ReactNode;
    footerWithBorder?: boolean;
    boldText?: boolean;
    bigText?: boolean;
    // Extra padding for the textfield container
    extraPadding?: boolean;
    shouldGrowVertically?: boolean;
    minHeight?: number;
    staticLineHeight?: boolean;
    borderVariant?: 'default' | 'ai-purple';
    // redux-form uses a different type of event than our components,
    // so we're typing this as "any" for now
    onFocus?: (event?: any) => void;
};

/**
 * Use this component for text input in forms.  It is completely
 * stateless and controlled, so the parent will need to manage its value.
 */
export class TextField extends React.PureComponent<TextFieldProps> {
    static displayName = 'TextField';
    node: HTMLInputElement | null | undefined;

    static defaultProps = {
        value: '',
        size: 'normal',
    };

    componentDidMount() {
        // Not sure why, but autoFocus isn't working, and a setImmediate is needed.
        setImmediate(() => {
            if (this.node && this.props.focusOnMount) {
                this.node.focus();

                // Multiline prop makes this render a textarea, which has
                // .focus() behavior to set the cursor to the beginning instead
                // of the end, so we need to manually set the cursor.
                if (this.props.multiline && this.node && this.node.value) {
                    const length = this.node.value.length;
                    this.node.setSelectionRange(length, length);
                }
            }
        });
    }

    render() {
        const {
            autocomplete,
            uncontrolled,
            multiline,
            blinkSuccess,
            hasError,
            errorMessage,
            placeholder,
            noBorder,
            noPadding,
            fitContent,
            size,
            selectOnFocus, // eslint-disable-line @typescript-eslint/no-unused-vars
            textFieldRef,
            // We don't want to spread in onChange, since we use `onInput` instead, and can't have both
            onChange, // eslint-disable-line @typescript-eslint/no-unused-vars
            // We don't want to spread in onEnter, either
            onEnter, // eslint-disable-line @typescript-eslint/no-unused-vars
            onEscape, // eslint-disable-line @typescript-eslint/no-unused-vars
            onFocus, // eslint-disable-line @typescript-eslint/no-unused-vars
            onKeyDown,
            inputUnit,
            focusOnMount, // eslint-disable-line @typescript-eslint/no-unused-vars
            ignorePasswordAutoFill,
            textAlign,
            customFooter,
            footerWithBorder,
            extraPadding,
            boldText,
            bigText,
            shouldGrowVertically,
            minHeight,
            staticLineHeight,
            borderVariant,
            rows,
            ...restProps
        } = this.props;

        const NodeType = multiline ? 'textarea' : 'input';
        const styleNames = classnames({
            [styles.textfield]: !multiline,
            [styles['textfield--text-align-left']]: textAlign === 'left',
            [styles['textfield--text-align-center']]: textAlign === 'center',
            [styles['textfield--text-align-right']]: textAlign === 'right',
            [styles['textfield--no-border']]: noBorder || customFooter,
            [styles['textfield--no-padding']]: noPadding,
            [styles['textfield--bordered']]: !noBorder && !customFooter,
            [styles['textfield--multiline']]: multiline,
            [styles['textfield--with-footer']]: customFooter,
            [styles['textfield--success']]: blinkSuccess,
            [styles['textfield--error']]: hasError,
            [styles['textfield--big']]: size === 'big',
            [styles['textfield--mini']]: size === 'mini',
            [styles['textfield--small']]: size === 'small',
            [styles['textfield--medium']]: size === 'medium',
            [styles['textfield--expand']]: size === 'expand',
            [styles['textfield--error-message']]: Boolean(errorMessage),
            [styles['textfield--fit-content']]: fitContent,
            [styles['textfield-footer-with-border']]: footerWithBorder,
            [styles['textfield--extra-padding']]: extraPadding,
            [styles['textfield--bold-text']]: boldText,
            [styles['textfield--big-text']]: bigText,
            [styles['textfield--should-grow']]: shouldGrowVertically,
            [styles['textfield--static-line-height']]: staticLineHeight,
            [styles['textfield--border-ai-purple']]: borderVariant === 'ai-purple',
        });
        const classNames = classnames({
            'keeper-ignore': ignorePasswordAutoFill,
        });

        // If component is uncontrolled we don't want to add onInput or value
        // to the actual input
        const controlledProps = uncontrolled
            ? {
                  value: undefined,
              }
            : {
                  value: this.props.value,
                  // @ts-expect-error
                  onChange: (e) => this.handleChange(e),
              };
        // Have to set the ref depending on what type of ref we got in props.  It could be the old
        // style callback ref, or a ref created with `useRef` which will be an object with a `current` property.
        const ref =
            typeof textFieldRef === 'object' &&
            Object.prototype.hasOwnProperty.call(textFieldRef, 'current')
                ? textFieldRef
                : this.setRef;
        if (
            typeof textFieldRef === 'object' &&
            Object.prototype.hasOwnProperty.call(textFieldRef, 'current')
        ) {
            // If we're not using a callback ref, this.setRef isn't called, so we need to
            // set our internal node reference here.
            this.node = textFieldRef.current;
        }

        const style = {
            ...(multiline && rows ? {height: `${rows * 26}px`} : {}),
            minHeight: minHeight ? `${minHeight}px` : undefined,
        } as const;

        const inputElement = (
            <NodeType
                id={this.props.id}
                // If we have set rows, override the set height, allowing rows to show without scrolling
                style={style}
                // @ts-expect-error
                ref={ref}
                autoComplete={autocomplete ? 'on' : 'off'}
                onKeyPress={this.handleKeyPress}
                onKeyUp={this.handleKeyUp}
                onFocus={this.handleFocus}
                onKeyDown={onKeyDown}
                placeholder={errorMessage ? errorMessage : placeholder}
                // For 1Password
                data-1p-ignore={ignorePasswordAutoFill}
                // For dashlane
                data-dashlane-autofill={ignorePasswordAutoFill}
                // Generic hopefully catch all
                data-form-type={ignorePasswordAutoFill ? 'other' : null}
                {...restProps}
                {...controlledProps}
                className={classnames(classNames, styleNames)}
            />
        );

        if (customFooter) {
            return (
                <div className={styles['textfield-container']}>
                    {inputElement}
                    <div className={styles['textfield-footer']}>{customFooter}</div>
                    {inputUnit ? <span className={styles.unit}>{inputUnit}</span> : undefined}
                </div>
            );
        }

        return (
            <>
                {inputElement}
                {inputUnit ? <span className={styles.unit}>{inputUnit}</span> : undefined}
            </>
        );
    }

    setRef = (node?: HTMLInputElement | null) => {
        this.node = node;
        if (typeof this.props.textFieldRef === 'function') {
            this.props.textFieldRef(node);
        }
    };

    handleFocus = (e: React.ChangeEvent<any>) => {
        if (this.props.selectOnFocus && this.node) {
            this.node.select();
        }

        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    };

    handleChange(e: React.ChangeEvent<any>) {
        const newValue = e.target.value;
        if (this.props.value === newValue) return;
        if (this.props.onChange) {
            if (
                this.props.type === 'number' &&
                ((typeof this.props.max !== 'undefined' &&
                    Number(newValue) > Number(this.props.max)) ||
                    (typeof this.props.min !== 'undefined' &&
                        Number(newValue) < Number(this.props.min)))
            ) {
                // Don't even allow the form to be set, don't rely on validation
                // upon submission
                return;
            }
            this.props.onChange(e.target.value);
        }
    }

    handleKeyPress = (e: React.KeyboardEvent<any>) => {
        if (e.key === 'Enter' && this.props.onEnter) {
            this.props.onEnter(e);
        }
    };

    handleKeyUp = (e: React.KeyboardEvent<any>) => {
        if (e.keyCode === 27 && this.props.onEscape) {
            this.props.onEscape(e);
        }
    };
}
